import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Shield, ShieldCheck, UserX } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/user-roles")({
	component: UsersPage,
});

type SystemRole =
	| "super_admin"
	| "support_personnel"
	| "sales_personnel"
	| "crm_admin";

type User = {
	id: string;
	name: string;
	email: string;
	image: string | null;
	emailVerified: boolean;
	createdAt: Date;
	systemRoles: {
		isSuperAdmin: boolean;
		isSupportPersonnel: boolean;
		isSalesPersonnel: boolean;
		isCrmAdmin: boolean;
	};
};

const hasRole = (user: User, role: SystemRole): boolean => {
	switch (role) {
		case "super_admin":
			return user.systemRoles.isSuperAdmin;
		case "support_personnel":
			return user.systemRoles.isSupportPersonnel;
		case "sales_personnel":
			return user.systemRoles.isSalesPersonnel;
		case "crm_admin":
			return user.systemRoles.isCrmAdmin;
	}
};

const ROLE_LABEL_KEY: Record<SystemRole, string> = {
	super_admin: "admin:user_management.super_admin",
	support_personnel: "admin:user_management.support_personnel",
	sales_personnel: "admin:user_management.sales_personnel",
	crm_admin: "admin:user_management.crm_admin",
};

const ROLE_GRANT_KEY: Record<SystemRole, string> = {
	super_admin: "admin:user_management.grant_super_admin",
	support_personnel: "admin:user_management.grant_support",
	sales_personnel: "admin:user_management.grant_sales",
	crm_admin: "admin:user_management.grant_crm_admin",
};

const ROLE_REVOKE_KEY: Record<SystemRole, string> = {
	super_admin: "admin:user_management.revoke_super_admin",
	support_personnel: "admin:user_management.revoke_support",
	sales_personnel: "admin:user_management.revoke_sales",
	crm_admin: "admin:user_management.revoke_crm_admin",
};

const SYSTEM_ROLES: SystemRole[] = [
	"super_admin",
	"support_personnel",
	"sales_personnel",
	"crm_admin",
];

type RoleAction = {
	userId: string;
	userName: string;
	role: SystemRole;
	action: "grant" | "revoke";
};

function UsersPage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [domains] = useState(["planvera.io"]);
	const [roleAction, setRoleAction] = useState<RoleAction | null>(null);

	// Debounce search input
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);

		return () => clearTimeout(timer);
	}, [search]);

	// Fetch users - using queryKey to ensure re-fetching when search changes
	const queryKey = [
		"admin",
		"users",
		"listByDomain",
		{ domains, search: debouncedSearch, limit: 50, offset: 0 },
	];

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: async () => {
			// Type assertion needed since listByDomain is newly added
			const result = await (
				client.admin.users as typeof client.admin.users & {
					listByDomain: (input: {
						domains?: string[];
						search?: string;
						limit?: number;
						offset?: number;
					}) => Promise<{
						users: User[];
						total: number;
						limit: number;
						offset: number;
					}>;
				}
			).listByDomain({
				domains,
				search: debouncedSearch || undefined,
				limit: 50,
				offset: 0,
			});
			return result;
		},
		staleTime: 1 * 60 * 1000, // 1 minute
		gcTime: 5 * 60 * 1000, // 5 minutes
	});

	// Grant role mutation
	const grantRoleMutation = useMutation({
		mutationFn: async (input: { userId: string; role: SystemRole }) => {
			// Type assertion needed since grantSystemRole is newly added
			return await (
				client.admin.users as typeof client.admin.users & {
					grantSystemRole: (input: {
						userId: string;
						role: SystemRole;
					}) => Promise<{ success: boolean; message: string }>;
				}
			).grantSystemRole(input);
		},
		onSuccess: async (result: { success: boolean; message: string }) => {
			toast.success(result.message);
			// Invalidate all listByDomain queries to refresh the user list
			await queryClient.invalidateQueries({
				predicate: (query) =>
					Array.isArray(query.queryKey) &&
					query.queryKey[0] === "admin" &&
					query.queryKey[1] === "users" &&
					query.queryKey[2] === "listByDomain",
			});
			setRoleAction(null);
		},
		onError: (error) => {
			toast.error(error.message || t("admin:user_management.grant_role_error"));
		},
	});

	// Revoke role mutation
	const revokeRoleMutation = useMutation({
		mutationFn: async (input: { userId: string; role: SystemRole }) => {
			// Type assertion needed since revokeSystemRole is newly added
			return await (
				client.admin.users as typeof client.admin.users & {
					revokeSystemRole: (input: {
						userId: string;
						role: SystemRole;
					}) => Promise<{ success: boolean; message: string }>;
				}
			).revokeSystemRole(input);
		},
		onSuccess: async (result: { success: boolean; message: string }) => {
			toast.success(result.message);
			// Invalidate all listByDomain queries to refresh the user list
			await queryClient.invalidateQueries({
				predicate: (query) =>
					Array.isArray(query.queryKey) &&
					query.queryKey[0] === "admin" &&
					query.queryKey[1] === "users" &&
					query.queryKey[2] === "listByDomain",
			});
			setRoleAction(null);
		},
		onError: (error) => {
			toast.error(
				error.message || t("admin:user_management.revoke_role_error"),
			);
		},
	});

	const handleRoleAction = () => {
		if (!roleAction) {
			return;
		}

		if (roleAction.action === "grant") {
			grantRoleMutation.mutate({
				userId: roleAction.userId,
				role: roleAction.role,
			});
		} else {
			revokeRoleMutation.mutate({
				userId: roleAction.userId,
				role: roleAction.role,
			});
		}
	};

	const getRoleBadge = (user: User, role: SystemRole) => {
		if (!hasRole(user, role)) {
			return null;
		}
		return (
			<Badge variant="default" className="gap-1">
				<ShieldCheck className="h-3 w-3" />
				{t(ROLE_LABEL_KEY[role])}
			</Badge>
		);
	};

	const getRoleActions = (user: User) =>
		SYSTEM_ROLES.map((role) => {
			const granted = hasRole(user, role);
			return (
				<Button
					key={`${granted ? "revoke" : "grant"}-${role}`}
					variant="outline"
					size="sm"
					onClick={() =>
						setRoleAction({
							userId: user.id,
							userName: user.name,
							role,
							action: granted ? "revoke" : "grant",
						})
					}
				>
					{granted ? (
						<UserX className="mr-1 h-3 w-3" />
					) : (
						<Shield className="mr-1 h-3 w-3" />
					)}
					{t(granted ? ROLE_REVOKE_KEY[role] : ROLE_GRANT_KEY[role])}
				</Button>
			);
		});

	return (
		<div className="space-y-6">
			<div>
				<h1 className="font-bold text-3xl">
					{t("admin:user_management.title")}
				</h1>
				<p className="text-muted-foreground">
					{t("admin:user_management.subtitle")}
				</p>
			</div>

			<div className="rounded-lg border bg-card shadow-sm">
				<div className="border-b p-4">
					<div className="flex items-center gap-4">
						<div className="flex-1">
							<Input
								placeholder={t("admin:user_management.search_placeholder")}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="max-w-md"
							/>
						</div>
						<div className="text-muted-foreground text-sm">
							{t("admin:user_management.domain_filter", {
								domains: domains.join(", "),
							})}
						</div>
					</div>
				</div>

				<div className="p-4">
					{isLoading ? (
						<div className="space-y-3">
							{Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map(
								(id) => (
									<Skeleton key={id} className="h-16 w-full" />
								),
							)}
						</div>
					) : data && data.users.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("admin:user_management.user")}</TableHead>
									<TableHead>{t("admin:user_management.email")}</TableHead>
									<TableHead>{t("admin:user_management.roles")}</TableHead>
									<TableHead className="text-right">
										{t("admin:user_management.actions")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{data.users.map((user: User) => (
									<TableRow key={user.id}>
										<TableCell>
											<div className="flex items-center gap-3">
												<Avatar className="h-8 w-8">
													<AvatarImage src={user.image || undefined} />
													<AvatarFallback>
														{user.name.substring(0, 2).toUpperCase()}
													</AvatarFallback>
												</Avatar>
												<div>
													<div className="font-medium">{user.name}</div>
													{user.emailVerified && (
														<div className="text-muted-foreground text-xs">
															{t("admin:user_management.verified")}
														</div>
													)}
												</div>
											</div>
										</TableCell>
										<TableCell className="font-mono text-sm">
											{user.email}
										</TableCell>
										<TableCell>
											<div className="flex gap-2">
												{SYSTEM_ROLES.map((role) => (
													<Fragment key={role}>
														{getRoleBadge(user, role)}
													</Fragment>
												))}
												{!SYSTEM_ROLES.some((role) => hasRole(user, role)) && (
													<span className="text-muted-foreground text-sm">
														{t("admin:user_management.no_roles")}
													</span>
												)}
											</div>
										</TableCell>
										<TableCell>
											<div className="flex justify-end gap-2">
												{getRoleActions(user)}
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<div className="py-12 text-center">
							<p className="text-muted-foreground">
								{t("admin:user_management.no_users_found")}
							</p>
						</div>
					)}
				</div>

				{data && data.total > 0 && (
					<div className="border-t p-4">
						<p className="text-muted-foreground text-sm">
							{t("admin:user_management.showing_users", {
								count: data.users.length,
								total: data.total,
							})}
						</p>
					</div>
				)}
			</div>

			{/* Role Action Confirmation Dialog */}
			<AlertDialog
				open={!!roleAction}
				onOpenChange={(open) => !open && setRoleAction(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{roleAction?.action === "grant"
								? t("admin:user_management.confirm_grant_title")
								: t("admin:user_management.confirm_revoke_title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{roleAction
								? t(
										roleAction.action === "grant"
											? "admin:user_management.confirm_grant_description"
											: "admin:user_management.confirm_revoke_description",
										{
											role: t(ROLE_LABEL_KEY[roleAction.role]),
											user: roleAction.userName,
										},
									)
								: null}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel
							disabled={
								grantRoleMutation.isPending || revokeRoleMutation.isPending
							}
						>
							{t("common:cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleRoleAction}
							disabled={
								grantRoleMutation.isPending || revokeRoleMutation.isPending
							}
						>
							{grantRoleMutation.isPending || revokeRoleMutation.isPending
								? t("common:loading")
								: t("common:confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
